import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useAuth } from "../../contexts/AuthContext";

const MigrateUsers = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [usersNeedingApproval, setUsersNeedingApproval] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const { isAdmin } = useAuth();

  // Load users that need approval on component mount
  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        // Get all users
        const usersCollection = collection(db, "users");
        const userSnapshot = await getDocs(usersCollection);
        
        // Filter users that don't have approval status set
        const usersToApprove = userSnapshot.docs
          .filter(doc => {
            const userData = doc.data();
            return userData.approved !== true && userData.approved !== false;
          })
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        
        setUsersNeedingApproval(usersToApprove);
      } catch (error) {
        console.error("Error loading users:", error);
        setResult({
          success: false,
          message: `Error loading users: ${error.message}`
        });
      } finally {
        setLoadingUsers(false);
      }
    };
    
    loadUsers();
  }, []);

  const handleMigration = async () => {
    if (!isAdmin) {
      setResult({
        success: false,
        message: "Only administrators can perform this operation."
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Get all users that need approval
      let updatedCount = 0;
      let errorCount = 0;
      
      // Update each user to set approved=true if it's not already set
      const updatePromises = usersNeedingApproval.map(async (userData) => {
        try {
          // Set approved to true for existing users
          await updateDoc(doc(db, "users", userData.id), {
            approved: true
          });
          
          updatedCount++;
          return { success: true, id: userData.id };
        } catch (error) {
          console.error(`Error updating user ${userData.id}:`, error);
          errorCount++;
          return { success: false, id: userData.id, error };
        }
      });
      
      // Wait for all updates to complete
      const results = await Promise.all(updatePromises);
      
      // Update the list of users needing approval
      const successfulIds = results.filter(r => r.success).map(r => r.id);
      setUsersNeedingApproval(prev => prev.filter(u => !successfulIds.includes(u.id)));
      
      setResult({
        success: errorCount === 0,
        message: `Migration complete. Successfully updated ${updatedCount} users. ${errorCount > 0 ? `Failed to update ${errorCount} users.` : ''}`
      });
    } catch (error) {
      console.error("Error during migration:", error);
      setResult({
        success: false,
        message: `Migration failed: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Function to approve a single user
  const handleApproveUser = async (userId) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, "users", userId), {
        approved: true
      });
      
      // Remove the user from the list
      setUsersNeedingApproval(prev => prev.filter(u => u.id !== userId));
      
      setResult({
        success: true,
        message: "User approved successfully."
      });
    } catch (error) {
      console.error("Error approving user:", error);
      setResult({
        success: false,
        message: `Error approving user: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
        User Approval Status
      </h2>
      <p className="text-gray-600 dark:text-gray-300 mb-4">
        This tool will update all existing users in the database to set their approval status to "Approved" if it's not already set.
        This is a one-time operation to ensure backward compatibility with the new registration system.
      </p>
      
      {result && (
        <div className={`mb-4 p-4 ${result.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'} rounded-md`}>
          {result.message}
        </div>
      )}
      
      {loadingUsers ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500 mr-3"></div>
          <p>Loading users...</p>
        </div>
      ) : usersNeedingApproval.length > 0 ? (
        <>
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Users Needing Approval ({usersNeedingApproval.length})
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Role</th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {usersNeedingApproval.map(user => (
                    <tr key={user.id}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.name || 'N/A'}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.role || (user.isAdmin ? 'admin' : 'user')}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleApproveUser(user.id)}
                          disabled={loading}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          Approve
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <button
            onClick={handleMigration}
            disabled={loading || usersNeedingApproval.length === 0}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm disabled:opacity-50"
          >
            {loading ? "Approving All Users..." : "Approve All Users"}
          </button>
        </>
      ) : (
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-md text-green-700 dark:text-green-400">
          <p className="font-medium">All users have approval status set!</p>
          <p className="text-sm mt-1">There are no users that need to be migrated.</p>
        </div>
      )}
      
      <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md text-yellow-700 dark:text-yellow-400">
        <h3 className="font-medium mb-2">Security Rules Update Required</h3>
        <p className="text-sm">To enable automatic migration, update your Firestore security rules to allow admins to update other user documents:</p>
        <pre className="bg-gray-100 dark:bg-gray-800 p-2 rounded mt-2 text-xs overflow-x-auto">
          {`match /users/{userId} {
  allow read: if true;
  allow write: if request.auth != null && 
    (request.auth.uid == userId || 
     get(/databases/$(database)/documents/users/$(request.auth.uid)).data.role == 'admin');
}`}
        </pre>
      </div>
    </div>
  );
};

export default MigrateUsers;
